import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';


//* MARK: Animation System
// ----------------------------------------------------------------------------

type AnimationDefinition = {
    startTime: number;
    duration: number;
    init?: (() => any) | null;
    update: (progress: number, anim?: Animation) => void;
};

class Animation {
    startTime: number;
    duration: number;
    initCallback?: (() => any) | null;
    updateCallback: (progress: number, anim?: Animation) => void;
    isFinished: boolean = false;
    data?: any;

    constructor(def: AnimationDefinition) {
        this.startTime = def.startTime;
        this.duration = def.duration;
        this.updateCallback = def.update;
        this.initCallback = def.init;
    }

    init() {
        if (this.initCallback) {
            this.data = this.initCallback();
        }
    }

    tick(currentTime: number) {
        if (currentTime >= this.startTime && !this.isFinished) {
            const elapsed = currentTime - this.startTime;
            const progress = Math.min(elapsed / this.duration, 1.0);
            this.updateCallback(progress, this);
            if (progress >= 1.0) this.isFinished = true;
        }
    }
};

class AnimationCollection {
    animations: Animation[] = [];
    add(AnimationDefinition: AnimationDefinition) {
        const anim = new Animation(AnimationDefinition);
        this.animations.push(anim);
    }

    initAll() {
        this.animations.forEach((anim) => anim.init());
    }

    tickAll(currentTime: number) {
        for (let i = this.animations.length - 1; i >= 0; i--) {
            const anim = this.animations[i];
            anim.tick(currentTime);
            if (anim.isFinished) {
                this.animations.splice(i, 1);
            }
        }
    }
};


//* MARK: Shaders
// ----------------------------------------------------------------------------

const vertexShader = /* glsl */ `
    varying vec2 vUv;
    void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
    }
`;

const stairsFragmentShader = /* glsl */ `
    uniform sampler2D uTexture;
    uniform float reveal;
    varying vec2 vUv;

    float rand(vec2 n) { 
        return fract(sin(dot(n, vec2(12.9898, 78.233))) * 43758.5453); 
    }
        
    float noise(vec2 n) {
        const vec2 d = vec2(0.0, 1.0);
        vec2 b = floor(n), f = smoothstep(vec2(0.0), vec2(1.0), fract(n));
        return mix(mix(rand(b), rand(b + d.yx), f.x), mix(rand(b + d.xy), rand(b + d.yy), f.x), f.y);
    }

    void main() {
        vec4 color = texture2D(uTexture, vUv);
        vec2 noiseUv = vUv * 10.0;
        noiseUv.x *= 3.0;
        float noise = noise(noiseUv);
        float limit = vUv.y + noise * 0.1;
        color.a *= smoothstep(limit - 0.03, limit + 0.05, reveal * 1.1);
        gl_FragColor = color;
    }
`;

const basicFragmentShader = /* glsl */ `
    uniform sampler2D uTexture;
    uniform float alpha;
    varying vec2 vUv;

    void main() {
        vec4 color = texture2D(uTexture, vUv);
        color.a *= alpha;
        gl_FragColor = color;
    }
`;


//* MARK: 3D Setup
// ----------------------------------------------------------------------------

const meshLoader = new GLTFLoader();

const visualsFrame = document.getElementById('top-visuals-frame') as HTMLDivElement | null;
if (visualsFrame) initTopAnimation(visualsFrame);

async function initTopAnimation(visualsFrame: HTMLDivElement) {
    const sceneWidth = visualsFrame.clientWidth;
    const sceneHeight = visualsFrame.clientHeight;
    const scene = new THREE.Scene();
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(sceneWidth, sceneHeight);
    renderer.setClearColor(0x202020, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace; // Ensure proper color space
    visualsFrame.appendChild(renderer.domElement);
    renderer.domElement.id = 'home-top-animation';
    let importedScene: THREE.Group;

    // Wait for renderer to be ready
    await new Promise((resolve) => {
        requestAnimationFrame(() => resolve(true));
    });

    const gltf = await meshLoader.loadAsync('/models/home-top.glb');
    importedScene = gltf.scene;
    scene.add(gltf.scene);
    const allMeshes = new Array<THREE.Mesh>();
    const camera: THREE.PerspectiveCamera = getChildObject3D(importedScene, 'Camera') as THREE.PerspectiveCamera;
    const shadow: THREE.Mesh = getChildObject3D(importedScene, 'Shadow') as THREE.Mesh;
    const paperGreen = getChildObject3D(importedScene, 'Paper_Green') as THREE.Mesh;
    const paperRed = getChildObject3D(importedScene, 'Paper_Red') as THREE.Mesh;
    const paperPurple = getChildObject3D(importedScene, 'Paper_Purple') as THREE.Mesh;
    const guyParent = getChildObject3D(importedScene, 'Character') as THREE.Object3D;
    const guyArmLeft = getChildObject3D(guyParent, 'Char_Arm_L') as THREE.Object3D;
    guyArmLeft.renderOrder = -1;
    const guyArmRight = getChildObject3D(guyParent, 'Char_Arm_R') as THREE.Object3D;
    const guyLegRight = getChildObject3D(guyParent, 'Char_Leg_R') as THREE.Object3D;
    const sparkle1 = getChildObject3D(importedScene, 'Sparkle_1') as THREE.Mesh;
    const sparkle2 = getChildObject3D(importedScene, 'Sparkle_2') as THREE.Mesh;

    // Load the texture
    const textureLoader = new THREE.TextureLoader();
    const spritesheetTexture = await textureLoader.loadAsync('/img/home-top-spritesheet.png');
    
    // Configure texture settings
    spritesheetTexture.wrapS = THREE.ClampToEdgeWrapping;
    spritesheetTexture.wrapT = THREE.ClampToEdgeWrapping;
    spritesheetTexture.magFilter = THREE.LinearFilter;
    spritesheetTexture.minFilter = THREE.LinearMipmapLinearFilter;
    spritesheetTexture.generateMipmaps = true;
    spritesheetTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();
    spritesheetTexture.flipY = false; // Try setting this to true if still looks wrong

    const stairsMaterial = new THREE.ShaderMaterial({
        uniforms: {
            uTexture: { value: spritesheetTexture },
            reveal: { value: 0.0 }
        },
        vertexShader,
        fragmentShader: stairsFragmentShader,
        transparent: true,
        depthWrite: false,
        alphaTest: 0.01,
        depthTest: false,
        toneMapped: false
    });

    const basicMaterial = new THREE.ShaderMaterial({
        uniforms: {
            uTexture: { value: spritesheetTexture },
            alpha: { value: 1.0 }
        },
        vertexShader,
        fragmentShader: basicFragmentShader,
        transparent: true,
        depthWrite: false,
        alphaTest: 0.01,
        depthTest: false,
        toneMapped: false
    });
    
    importedScene.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
            allMeshes.push(child as THREE.Mesh);
        }
    });
    allMeshes.forEach((mesh) => {
        if (mesh.name === 'Shadow') mesh.renderOrder = -1;
        if (mesh.name === 'Stairs') {
            mesh.material = stairsMaterial;
            return;
        }
        // Create a new material instance for each mesh to avoid sharing issues
        mesh.material = basicMaterial.clone();
    });

    //* MARK: 3D Animations
    // ------------------------------------------------------------------------
    
    const animations = new AnimationCollection();

    function papersRevealAnimation(mesh: THREE.Mesh, startTime: number, duration: number) {
        return {
            startTime, duration,
            init: () => {
                (mesh.material as THREE.ShaderMaterial).uniforms.alpha.value = 0.0;
                const finalPosition = mesh.position.clone();
                const finalRotation = mesh.rotation.clone();
                mesh.position.y -= 1;
                mesh.position.z -= 0.5;
                mesh.position.x += 0.5;
                mesh.rotation.y += THREE.MathUtils.degToRad(20);
                mesh.rotation.z += THREE.MathUtils.degToRad(90);
                const startPosition = mesh.position.clone();
                const startRotation = mesh.rotation.clone();
                return { startPosition, finalPosition, startRotation, finalRotation };
            },
            update: (progress: number, anim?: Animation) => {
                (mesh.material as THREE.ShaderMaterial).uniforms.alpha.value = progress;
                if (anim && anim.data) {
                    const eased = overshootEasing(progress);
                    mesh.position.lerpVectors(anim.data.startPosition, anim.data.finalPosition, eased);
                    const rotationVector = new THREE.Vector3().lerpVectors(
                        new THREE.Vector3(anim.data.startRotation.x, anim.data.startRotation.y, anim.data.startRotation.z),
                        new THREE.Vector3(anim.data.finalRotation.x, anim.data.finalRotation.y, anim.data.finalRotation.z),
                        eased
                    );
                    mesh.rotation.set(rotationVector.x, rotationVector.y, rotationVector.z);
                }
            }
        };
    }

    function guyJumpInAnimation(mesh: THREE.Mesh, startTime: number, duration: number) {
        return {
            startTime, duration,
            init: () => {
                const finalPosition = mesh.position.clone();
                const finalScale = mesh.scale.clone();
                const armLeftRotation = guyArmLeft.quaternion.clone();
                const armRightRotation = guyArmRight.quaternion.clone();
                const legRightRotation = guyLegRight.quaternion.clone();
                mesh.position.y -= 1.0;
                mesh.position.x -= 0.2;
                mesh.scale.set(0, 0, 0);
                guyArmLeft.rotation.y -= 0.5;
                guyArmRight.rotation.y += 0.5;
                guyLegRight.rotation.y -= 0.9;
                const startPosition = mesh.position.clone();
                const startScale = mesh.scale.clone();
                const armLeftStartRotation = guyArmLeft.quaternion.clone();
                const armRightStartRotation = guyArmRight.quaternion.clone();
                const legRightStartRotation = guyLegRight.quaternion.clone();
                return { 
                    startPosition, finalPosition, 
                    startScale, finalScale, 
                    armLeftStartRotation, armLeftRotation,
                    armRightRotation, armRightStartRotation,
                    legRightRotation, legRightStartRotation
                };
            },
            update: (progress: number, anim?: Animation) => {
                if (anim && anim.data) {
                    const eased = overshootEasing(progress);
                    mesh.position.lerpVectors(anim.data.startPosition, anim.data.finalPosition, eased);
                    mesh.scale.lerpVectors(anim.data.startScale, anim.data.finalScale, eased);
                    const armProgress = inverseLerp(0.2, 1.0, progress);
                    const easedArm = overshootEasing(armProgress);
                    guyArmLeft.quaternion.slerpQuaternions(anim.data.armLeftStartRotation, anim.data.armLeftRotation, easedArm);
                    guyArmRight.quaternion.slerpQuaternions(anim.data.armRightStartRotation, anim.data.armRightRotation, easedArm);
                    guyLegRight.quaternion.slerpQuaternions(anim.data.legRightStartRotation, anim.data.legRightRotation, easedArm);
                }
            }
        };
    }

    // Global animation timings
    const globalDelay = 0.2;
    const stairsRevealTime = globalDelay + 0.1;
    const shadowRevealTime = globalDelay + 0.8;
    const papersRevealTime = globalDelay + 0.4;
    const guyJumpInTime = globalDelay + 0.9;
    const sparklesAppearTime = globalDelay + 1.5;

    // Stairs reveal animation
    animations.add({ startTime: stairsRevealTime, duration: 2.0,
        init: () => { stairsMaterial.uniforms.reveal.value = 0.0; },
        update: (progress) => { 
            const eased = 1 - Math.pow(1 - progress, 5);
            stairsMaterial.uniforms.reveal.value = eased; 
        }
    });
    // Shadow reveal (fade in)
    animations.add({ startTime: shadowRevealTime, duration: 0.8,
        init: () => { (shadow.material as THREE.ShaderMaterial).uniforms.alpha.value = 0.0; },
        update: (progress) => { (shadow.material as THREE.ShaderMaterial).uniforms.alpha.value = progress; }
    });
    animations.add(papersRevealAnimation(paperPurple, papersRevealTime, 0.5));
    animations.add(papersRevealAnimation(paperRed, papersRevealTime + 0.1, 0.5));
    animations.add(papersRevealAnimation(paperGreen, papersRevealTime + 0.2, 0.5));
    animations.add(guyJumpInAnimation(guyParent as THREE.Mesh, guyJumpInTime, 0.6));
    animations.add({ startTime: sparklesAppearTime, duration: 0.25,
        init: () => { sparkle1.scale.set(0, 0, 0); },
        update: (progress: number) => {
            const eased = overshootEasing(progress, 5);
            sparkle1.scale.set(eased, eased, eased);
        }
    });
    animations.add({ startTime: sparklesAppearTime + 0.1, duration: 0.25,
        init: () => { sparkle2.scale.set(0, 0, 0); },
        update: (progress: number) => {
            const eased = overshootEasing(progress, 5);
            sparkle2.scale.set(eased, eased, eased);
        }
    });


    //* MARK: 3D Lifecycle Events
    // ------------------------------------------------------------------------

    const cameraStartPos = camera!.position.clone();
    let cameraResponsiveOffset = new THREE.Vector3(0, 0, 0);
    let lastTickTimeMs = 0;
    let startTimeMs = 0;
    const mousePosition = new THREE.Vector2(0, 0);
    const greenFileStartPos: THREE.Vector3 = paperGreen.position.clone();
    const redFileStartPos: THREE.Vector3 = paperRed.position.clone();
    const purpleFileStartPos: THREE.Vector3 = paperPurple.position.clone();
    const guyStartPos: THREE.Vector3 = guyParent.position.clone();
    const easedMousePosition = new THREE.Vector2(0, 0);
    let hasMovedMouse = false;
    let timeSinceFirstMouseMove = 0;

    function start() {
        lastTickTimeMs = performance.now();
        startTimeMs = performance.now();

        camera.aspect = sceneWidth / sceneHeight;
        camera.updateProjectionMatrix();
        camera.lookAt(-1.0, 0.300702, -0.057039);
        onResize();
        animations.initAll();
        renderer.render(scene, camera);
        // Get mouse position:

    }

    function update(timeMs: number) {
        const time = (timeMs / 1000) - (startTimeMs / 1000);
        const deltaTime = (timeMs - lastTickTimeMs) / 1000;
        lastTickTimeMs = timeMs;
        if (hasMovedMouse) timeSinceFirstMouseMove += deltaTime;
        if (timeSinceFirstMouseMove < 1.0) easedMousePosition.lerp(mousePosition, deltaTime * (timeSinceFirstMouseMove * 40));
        else easedMousePosition.copy(mousePosition);
        camera.position.x = cameraStartPos.x + cameraResponsiveOffset.x + easedMousePosition.x * 0.90;
        camera.position.y = cameraStartPos.y + cameraResponsiveOffset.y + easedMousePosition.y * 0.35;
        camera.position.z = cameraStartPos.z + cameraResponsiveOffset.z;
        paperGreen.position.y = greenFileStartPos.y + Math.sin(time * 3.0) * 0.01 * clamp01(time - papersRevealTime);
        paperRed.position.y = redFileStartPos.y + Math.sin(time * 2.5 + 1.0) * 0.01 * clamp01(time - papersRevealTime);
        paperPurple.position.y = purpleFileStartPos.y + Math.sin(time * 2.8 + 2.0) * 0.01 * clamp01(time - papersRevealTime);
        guyParent.position.y = guyStartPos.y + Math.sin(time * 2.0) * 0.015 * clamp01(time - guyJumpInTime);
        camera.lookAt(-1.0 + cameraResponsiveOffset.x, 0.500702, 0.57039);
        animations.tickAll(time);
        renderer.render(scene, camera);
        requestAnimationFrame(update);
    }

    visualsFrame.addEventListener('resize', onResize);
    window.addEventListener('resize', onResize);
    function onResize() {
        const newWidth = visualsFrame.clientWidth;
        const newHeight = visualsFrame.clientHeight;
        const aspectRatio = newWidth / newHeight;
        renderer.setSize(newWidth, newHeight);
        if (camera) {
            camera.aspect = newWidth / newHeight;
            camera.updateProjectionMatrix();
        }
        cameraResponsiveOffset.x = lerp(0.1, -0.5, clamp01(inverseLerp(1.60, 0.80, aspectRatio)));
        cameraResponsiveOffset.y = lerp(0, 2.5, clamp01(inverseLerp(1.60, 0.80, aspectRatio)));
        cameraResponsiveOffset.z = lerp(0, 1.5, clamp01(inverseLerp(1.60, 0.80, aspectRatio)));
    }
    
    window.addEventListener('mousemove', onMove);
    function onMove(event: MouseEvent) {
        const mouseX = (event.clientX / window.innerWidth) * 2 - 1;
        const mouseY = -(event.clientY / window.innerHeight) * 2 + 1;
        mousePosition.set(mouseX, mouseY);
        if (!hasMovedMouse) hasMovedMouse = true;
    }
    
    start();
    update(startTimeMs);
}


//* MARK: Simple CSS Transitions
// ----------------------------------------------------------------------------

const tablet1Element = document.getElementById('home-tablet-1') as HTMLDivElement | null;
if (tablet1Element) {
    const images = tablet1Element.getElementsByTagName('img');

    async function revealHomeTablet1() {
        tablet1Element!.classList.add('active');
        images[0].classList.add('active');
        await waitForSeconds(0.3);
        for (let i = 1; i < images.length; i++) {
            images[i].classList.add('active');
            await waitForSeconds(0.1);
        }
    }

    // Trigger animation when element comes into view
    const observer1 = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                revealHomeTablet1();
                observer1.unobserve(entry.target);
            }
        });
    }, { threshold: 0.3, rootMargin: '-50px 0px' });
    
    observer1.observe(tablet1Element);
}

const tablet2Element = document.getElementById('home-tablet-2') as HTMLDivElement | null;
if (tablet2Element) {
    const images = tablet2Element.getElementsByTagName('img');
    async function revealHomeTablet2() {
        tablet2Element!.classList.add('active');
        images[0].classList.add('active');
        await waitForSeconds(0.3);
        for (let i = 1; i < images.length; i++) {
            images[i].classList.add('active');
            await waitForSeconds(0.08);
        }
    }
    
    // Trigger animation when element comes into view
    const observer2 = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                revealHomeTablet2();
                observer2.unobserve(entry.target);
            }
        });
    }, { threshold: 0.3, rootMargin: '-30px 0px' });
    
    observer2.observe(tablet2Element);
}

const endCtaImageElement = document.getElementById('end-cta-image') as HTMLDivElement | null;
if (endCtaImageElement) {
    const images = endCtaImageElement.getElementsByTagName('img');
    async function revealEndCtaImages() {
        for (let i = 0; i < images.length; i++) {
            images[i].classList.add('active');
            await waitForSeconds(0.2);
        }
    }
    
    // Trigger animation when element comes into view
    const observer3 = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                revealEndCtaImages();
                observer3.unobserve(entry.target);
            }
        });
    }, { threshold: 0.3, rootMargin: '-10px 0px' });
    
    observer3.observe(endCtaImageElement);
}

const homeLoadingOverlay = document.getElementById('loading-overlay') as HTMLDivElement;
if (homeLoadingOverlay) {
    async function hideLoadingOverlay() {
        await waitForSeconds(0.15);
        homeLoadingOverlay.style.opacity = '0';
    }
    hideLoadingOverlay();
}


//* MARK: Utils
// ----------------------------------------------------------------------------

function getChildObject3D(parent: THREE.Object3D, name: string): THREE.Object3D | null {
    return parent.children.find((child) => child.name === name) || null;
}

function overshootEasing(t: number, overshootFactor = 1.5): number {
    t -= 1;
    return t * t * ((overshootFactor + 1) * t + overshootFactor) + 1;
}

function inverseLerp(a: number, b: number, value: number): number {
    if (a !== b) {
        return (value - a) / (b - a);
    } else {
        return 0;
    }
}

function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}

function clamp01(value: number): number {
    return Math.min(Math.max(value, 0), 1);
}

function waitForSeconds(seconds: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(() => resolve(), seconds * 1000);
    });
}